import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';

interface Photo {
  url: string;
  caption?: string;
}

interface PhotoLightboxProps {
  isOpen: boolean;
  onClose: () => void;
  photos: Photo[];
  initialIndex?: number;
  slideshowInterval?: number;
}

const ZOOM_LEVELS = [1, 1.5, 2, 3];
const SPEED_OPTIONS = [
  { label: '0.5×', ms: 6000 },
  { label: '1×', ms: 3000 },
  { label: '1.5×', ms: 2000 },
  { label: '2×', ms: 1500 },
  { label: '3×', ms: 1000 },
];

export default function PhotoLightbox({
  isOpen,
  onClose,
  photos,
  initialIndex = 0,
}: PhotoLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageKey, setImageKey] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [speedIndex, setSpeedIndex] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });

  const currentSpeed = SPEED_OPTIONS[speedIndex];

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    }
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (isOpen && photos[currentIndex]) {
      setIsLoading(true);
      setImageError(false);
      setImageKey(prev => prev + 1);
      setZoomLevel(1);
      setPanOffset({ x: 0, y: 0 });
    }
  }, [currentIndex, isOpen, photos]);

  const clearSlideshow = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startSlideshow = useCallback((speedMs: number) => {
    clearSlideshow();
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1));
    }, speedMs);
  }, [photos.length, clearSlideshow]);

  useEffect(() => {
    if (isPlaying && isOpen && photos.length > 1) {
      startSlideshow(currentSpeed.ms);
    } else {
      clearSlideshow();
    }
    return clearSlideshow;
  }, [isPlaying, isOpen, photos.length, speedIndex]);

  useEffect(() => {
    return clearSlideshow;
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? photos.length - 1 : prev - 1));
  }, [photos.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev === photos.length - 1 ? 0 : prev + 1));
  }, [photos.length]);

  const zoomIn = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      return idx < ZOOM_LEVELS.length - 1 ? ZOOM_LEVELS[idx + 1] : prev;
    });
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel(prev => {
      const idx = ZOOM_LEVELS.indexOf(prev);
      if (idx <= 0) return 1;
      const next = ZOOM_LEVELS[idx - 1];
      if (next === 1) setPanOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel <= 1) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...panOffset };
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    setPanOffset({ x: panStart.current.x + dx, y: panStart.current.y + dy });
  };

  const handleMouseUp = () => { isDragging.current = false; };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (zoomLevel <= 1) return;
    const touch = e.touches[0];
    isDragging.current = true;
    dragStart.current = { x: touch.clientX, y: touch.clientY };
    panStart.current = { ...panOffset };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - dragStart.current.x;
    const dy = touch.clientY - dragStart.current.y;
    setPanOffset({ x: panStart.current.x + dx, y: panStart.current.y + dy });
    e.stopPropagation();
  };

  const handleTouchEnd = () => { isDragging.current = false; };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      switch (e.key) {
        case 'ArrowLeft':
          if (zoomLevel === 1) { goToPrevious(); if (isPlaying) setIsPlaying(false); }
          break;
        case 'ArrowRight':
          if (zoomLevel === 1) { goToNext(); if (isPlaying) setIsPlaying(false); }
          break;
        case 'Escape':
          if (zoomLevel > 1) { resetZoom(); } else { onClose(); }
          break;
        case ' ':
          e.preventDefault();
          setIsPlaying(p => !p);
          break;
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
          zoomOut();
          break;
        case '0':
          resetZoom();
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, zoomLevel, isPlaying, goToPrevious, goToNext, zoomIn, zoomOut, resetZoom, onClose]);

  if (!photos || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];
  const canZoomIn = ZOOM_LEVELS.indexOf(zoomLevel) < ZOOM_LEVELS.length - 1;
  const canZoomOut = zoomLevel > 1;

  const handleNavPrev = () => { goToPrevious(); if (isPlaying) setIsPlaying(false); };
  const handleNavNext = () => { goToNext(); if (isPlaying) setIsPlaying(false); };

  const handleSpeedChange = (idx: number) => {
    setSpeedIndex(idx);
    setShowSpeedMenu(false);
    if (isPlaying) {
      clearSlideshow();
      startSlideshow(SPEED_OPTIONS[idx].ms);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/95 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10">
          <div className="h-full w-full">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full h-full flex flex-col transform transition-all">

                {/* Top controls */}
                <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
                  {/* Zoom controls */}
                  <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                    <button
                      onClick={zoomOut}
                      disabled={!canZoomOut}
                      className="p-1.5 text-white disabled:opacity-30 hover:text-golf-orange transition-colors"
                      title="Zoom out (-)"
                    >
                      <MagnifyingGlassMinusIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={resetZoom}
                      className="px-2 text-white text-xs font-medium hover:text-golf-orange transition-colors min-w-[2.5rem] text-center"
                      title="Reset zoom (0)"
                    >
                      {Math.round(zoomLevel * 100)}%
                    </button>
                    <button
                      onClick={zoomIn}
                      disabled={!canZoomIn}
                      className="p-1.5 text-white disabled:opacity-30 hover:text-golf-orange transition-colors"
                      title="Zoom in (+)"
                    >
                      <MagnifyingGlassPlusIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Slideshow controls */}
                  {photos.length > 1 && (
                    <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
                      <button
                        onClick={() => setIsPlaying(p => !p)}
                        className="p-1.5 text-white hover:text-golf-orange transition-colors"
                        title={isPlaying ? 'Pause (Space)' : 'Play slideshow (Space)'}
                      >
                        {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setShowSpeedMenu(s => !s)}
                          className="px-2 text-white text-xs font-medium hover:text-golf-orange transition-colors min-w-[2.5rem] text-center"
                          title="Slideshow speed"
                        >
                          {currentSpeed.label}
                        </button>
                        {showSpeedMenu && (
                          <div className="absolute top-full right-0 mt-2 bg-black/90 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden min-w-[5rem] shadow-xl">
                            {SPEED_OPTIONS.map((opt, i) => (
                              <button
                                key={opt.label}
                                onClick={() => handleSpeedChange(i)}
                                className={`w-full px-4 py-2.5 text-xs text-left transition-colors ${
                                  i === speedIndex
                                    ? 'bg-golf-orange/20 text-golf-orange font-semibold'
                                    : 'text-white hover:bg-white/10'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-2 text-white text-xs font-medium">
                    {currentIndex + 1} / {photos.length}
                  </div>

                  <button
                    onClick={onClose}
                    className="rounded-full bg-black/60 backdrop-blur-sm p-2 text-white hover:bg-black/80 hover:text-golf-orange transition-colors"
                    title="Close (Esc)"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Reset zoom button shown when zoomed */}
                {zoomLevel > 1 && (
                  <div className="absolute top-4 left-4 z-50">
                    <button
                      onClick={resetZoom}
                      className="flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-sm px-3 py-2 text-white text-xs font-medium hover:bg-black/80 transition-colors"
                      title="Reset zoom (0)"
                    >
                      <ArrowsPointingOutIcon className="h-4 w-4" />
                      Reset zoom
                    </button>
                  </div>
                )}

                {/* Nav arrows */}
                {photos.length > 1 && zoomLevel === 1 && (
                  <>
                    <button
                      onClick={handleNavPrev}
                      className="absolute left-4 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/60 backdrop-blur-sm p-3 text-white hover:bg-black/80 hover:text-golf-orange transition-colors"
                      title="Previous (←)"
                    >
                      <ChevronLeftIcon className="h-6 w-6" />
                    </button>
                    <button
                      onClick={handleNavNext}
                      className="absolute right-4 top-1/2 z-50 -translate-y-1/2 rounded-full bg-black/60 backdrop-blur-sm p-3 text-white hover:bg-black/80 hover:text-golf-orange transition-colors"
                      title="Next (→)"
                    >
                      <ChevronRightIcon className="h-6 w-6" />
                    </button>
                  </>
                )}

                {/* Image area */}
                <div
                  className="flex-1 min-h-0 relative overflow-hidden"
                  style={{ cursor: zoomLevel > 1 ? (isDragging.current ? 'grabbing' : 'grab') : 'default' }}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
                    </div>
                  )}

                  {imageError ? (
                    <div key={imageKey} className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <p className="text-lg mb-4">Failed to load image</p>
                      <button
                        onClick={() => { setImageError(false); setIsLoading(true); }}
                        className="px-4 py-2 bg-golf-orange text-white rounded-lg hover:bg-golf-green transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <img
                      key={imageKey}
                      src={currentPhoto.url}
                      alt={currentPhoto.caption || `Photo ${currentIndex + 1}`}
                      className="absolute inset-0 w-full h-full object-contain select-none animate-[fadeIn_0.4s_ease-in-out]"
                      style={{
                        transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                        transformOrigin: 'center center',
                        transition: 'transform 0.2s',
                      }}
                      draggable={false}
                      onLoad={() => { setIsLoading(false); setImageError(false); }}
                      onError={() => { setIsLoading(false); setImageError(true); }}
                      onDoubleClick={() => zoomLevel === 1 ? zoomIn() : resetZoom()}
                    />
                  )}
                </div>

                {/* Bottom bar: caption, thumbnails, hints */}
                <div className="flex flex-col items-center gap-2 px-4 py-3 bg-black/60 backdrop-blur-sm">
                  {currentPhoto.caption && (
                    <p
                      key={`caption-${imageKey}`}
                      className="text-white text-center text-sm max-w-4xl animate-[fadeIn_0.5s_ease-in-out]"
                    >
                      {currentPhoto.caption}
                    </p>
                  )}

                  {photos.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto max-w-full pb-1">
                      {photos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setCurrentIndex(index);
                            if (isPlaying) setIsPlaying(false);
                          }}
                          className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentIndex
                              ? 'border-golf-orange scale-110'
                              : 'border-transparent opacity-60 hover:opacity-100 hover:border-white/50'
                          }`}
                        >
                          <img
                            src={photo.url}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-4 text-white/30 text-[11px]">
                    <span>← → Navigate</span>
                    <span>Space Play/Pause</span>
                    <span>+ − Zoom</span>
                    <span>Double-click Zoom</span>
                    <span>0 Reset</span>
                    <span>Esc Close</span>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
